import { TicketTransferOption } from './types';

/**
 * 수강권 양도의 **입력 상한**을 정하는 곳.
 *
 * 배경: 예전에는 미래 일정이 1건이라도 있으면 양도가 통째로 막혔다. 서버가
 * "미래 일정 몫만 남기고 나머지는 양도" 로 바뀌면서 `maxTransferableCount` 를
 * 내려준다. 화면은 그 값을 그대로 상한으로 쓴다 — 프론트에서 다시 계산하지 않는다.
 *
 * 구버전 서버 호환: 이 필드가 없으면 `remainingCount` 로 폴백한다. 그러면 상한도
 * 안내 문구도 오늘과 같아서, 프론트가 서버보다 먼저 배포돼도 안전하다.
 */

type TransferLimitInput = Pick<
    TicketTransferOption,
    'remainingCount' | 'maxTransferableCount' | 'unconsumedFutureCount'
>;

/** 서버가 보낸 값 중 회차로 쓸 수 있는 것만 통과시킨다. 아니면 null(=값 없음). */
const toCount = (value: unknown): number | null => {
    if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) return null;
    return value;
};

export const resolveRemainingCount = (
    option: TransferLimitInput | null | undefined,
): number => toCount(option?.remainingCount) ?? 0;

/**
 * 양도 가능한 최대 회차.
 *
 * - 서버가 `maxTransferableCount` 를 주면 그 값(단, 잔여를 넘지 못하게 한 번 조인다).
 * - 없거나 숫자로 못 읽으면 `remainingCount` — 즉 구버전 서버에서는 오늘과 동일.
 */
export const resolveMaxTransferableCount = (
    option: TransferLimitInput | null | undefined,
): number => {
    const remaining = resolveRemainingCount(option);
    const max = toCount(option?.maxTransferableCount);
    if (max === null) return remaining;
    // 계약상 상한은 잔여 이하지만, 상한이 잔여를 넘는 응답을 그대로 믿어
    // 잔여보다 많이 보내면 서버가 409로 되돌려준다. 화면에서 먼저 막는다.
    return Math.min(max, remaining);
};

/** 상한이 잔여보다 작게 만든 미차감 예약 건수. 설명 문구에만 쓴다. */
export const resolveUnconsumedFutureCount = (
    option: TransferLimitInput | null | undefined,
): number => toCount(option?.unconsumedFutureCount) ?? 0;

/**
 * 상한이 잔여보다 작을 때 **이유를 설명하는** 회색 안내. 차단 문구가 아니다.
 *
 * 구버전 서버에서는 `unconsumedFutureCount` 가 없어 항상 null 이다.
 */
export const getTransferLimitNotice = (
    option: TransferLimitInput | null | undefined,
): string | null => {
    const remaining = resolveRemainingCount(option);
    const max = resolveMaxTransferableCount(option);
    const unconsumed = resolveUnconsumedFutureCount(option);

    if (unconsumed <= 0) return null;
    if (max >= remaining) return null;

    if (max <= 0) {
        return `대기 예약 ${unconsumed}건 몫을 남겨야 해서 지금은 양도할 수 있는 회차가 없습니다.`;
    }
    return `대기 예약 ${unconsumed}건 몫을 남겨 최대 ${max}회까지 양도할 수 있습니다.`;
};

/**
 * 서버가 양도를 막은 옵션에 붙이는 회색 사유.
 *
 * 만료는 화면이 따로 안내하고(만료 문구), 상한 때문에 0회가 된 경우는
 * {@link getTransferLimitNotice} 가 이미 설명하므로 여기서는 null 을 준다.
 * 이유를 못 받은 구버전 서버에서도 "왜 눌리지 않는지" 는 알려준다.
 */
export const getTransferUnavailableNotice = (
    option: TicketTransferOption | null | undefined,
): string | null => {
    if (!option || option.canTransfer) return null;
    if (option.isExpired) return null;
    if (getTransferLimitNotice(option)) return null;

    const reason = typeof option.unavailableReason === 'string' ? option.unavailableReason.trim() : '';
    return reason || '지금은 양도할 수 없는 수강권입니다.';
};

/** 입력한 회차가 상한 안에 있는지. 상한이 0이면 어떤 값도 유효하지 않다. */
export const isTransferCountWithinLimit = (
    option: TransferLimitInput | null | undefined,
    count: number,
): boolean => {
    const max = resolveMaxTransferableCount(option);
    return Number.isInteger(count) && count > 0 && max > 0 && count <= max;
};
